import { useMemo } from "react";
import {
  Column,
  FormField,
  Row,
  Select,
  Switch,
  Text,
  ExternalLinkIcon,
} from "@hightouch/htui";
import { produce } from "immer";
import { TraitParentModel } from "src/components/audiences/types";
import { Controller, useFormContext } from "react-hook-form";
import { useGetCatalogPredictorFeatureDefinitionsQuery } from "src/graphql";
import { ReadOnlyConditions } from "src/components/explore/read-only-conditions";
import { Link } from "src/router";
import { QueryBuilder } from "src/components/explore";
import { QueryBuilderProvider } from "src/components/explore/context/query-builder-context";
import { useFetchQueryBuilderParentModel } from "src/components/explore/hooks";
import { OnUpdate } from "src/components/explore/utils/condition-builders";
import { EventFilter } from "src/components/explore/visual/event-filter";
import {
  ConditionType,
  EventCondition,
  initialEventCondition,
} from "src/types/visual";
import { UpdaterEnforceDefined } from "src/utils/immutable-helpers";
import { CATALOG_ELIGIBILITY_RESTRICTIONS } from "src/components/traits/predictive-utils";
import { predictionUpdateFrequencyOptions } from "./predictive-trait-calculation";
import { CatalogFilter } from "./catalog-filter";

type Props = {
  parentModel: TraitParentModel;
  isCreating?: boolean;
};

export function CatalogRecommendationTraitCalculation(props: Props) {
  const catalogPredictorFeatureDefinitions =
    useGetCatalogPredictorFeatureDefinitionsQuery({
      parentModelId: props.parentModel.id,
    });

  const { watch, setValue } = useFormContext();
  const featureDefinitionId = watch("predictorDefinition.featureDefinitionId");
  const catalogId = watch("predictorDefinition.catalogId");

  return (
    <Column gap={6}>
      <FormField label="Select catalog">
        <Select
          isDisabled={!props.isCreating}
          value={catalogPredictorFeatureDefinitions.data?.predictor_feature_definitions.find(
            (pfd) => pfd.id === featureDefinitionId,
          )}
          placeholder="Select catalog"
          options={
            catalogPredictorFeatureDefinitions.data?.predictor_feature_definitions.map(
              (pfd) => ({
                label: pfd.name,
                value: pfd,
              }),
            ) ?? []
          }
          onChange={(value) => {
            if (value) {
              setValue("predictorDefinition.featureDefinitionId", value.id);
              setValue("predictorDefinition.catalogId", value.catalog_id);
            }
          }}
        />
      </FormField>
      <FormField
        label="Add filters"
        description="Specify which items to include from your catalog."
        isOptional
      >
        <Column
          borderWidth="hairline"
          borderStyle="solid"
          borderColor="base.border"
          borderRadius="md"
          overflowX="auto"
        >
          <Column p={4} minWidth="fit-content">
            <CatalogFilter
              leftModelId={catalogId}
              rightModelId={props.parentModel.id}
              connectionType={props.parentModel.connection?.type}
              formKey="predictorDefinition.catalogDefinition"
            />
          </Column>
        </Column>
      </FormField>
      <Controller
        name="predictorDefinition.audienceDefinition"
        render={({ field }) => (
          <FormField
            label="Who should this prediction apply to?"
            description="Filter to the users you want to score—based on something they’ve already done, like signing up, viewing a page, or starting checkout."
            isOptional
          >
            <Column
              p={4}
              borderWidth="hairline"
              borderStyle="solid"
              borderColor="base.border"
              borderRadius="md"
            >
              <QueryBuilder
                maxNestingDepth={0}
                filter={field.value}
                updateConditions={(updater) =>
                  field.onChange(produce(field.value, updater))
                }
                parentModel={props.parentModel}
                enableNestedFiltering={false}
                restrictions={CATALOG_ELIGIBILITY_RESTRICTIONS}
              />
            </Column>
          </FormField>
        )}
      />
      <Controller
        name="predictorDefinition.config.inferenceCadence"
        render={({ field }) => (
          <FormField
            label="How often should scores be updated?"
            description="Set how frequently predictions refresh using the latest data. Faster updates improve freshness but use more resources."
          >
            <Column gap={1}>
              <Select
                options={predictionUpdateFrequencyOptions}
                value={field.value ?? "weekly"}
                onChange={field.onChange}
              />
              <Text size="sm" color="text.tertiary">
                20 items automatically recommended per user
              </Text>
            </Column>
          </FormField>
        )}
      />
      <SuppressionFilterField parentModel={props.parentModel} />
    </Column>
  );
}

type SuppressionEventOption = {
  keyEventId: string;
  eventName: string;
  eventModelId: number;
  userRelationshipId: number;
};

function makeSuppressionFilterValue(event: SuppressionEventOption) {
  return {
    keyEventId: event.keyEventId,
    eventFilter: {
      ...initialEventCondition,
      type: ConditionType.Event,
      eventModelId: event.eventModelId,
      relationshipId: event.userRelationshipId,
    },
  };
}

function SuppressionFilterField({
  parentModel,
}: {
  parentModel: TraitParentModel;
}) {
  const { watch, setValue } = useFormContext();
  const featureDefinitionId = watch("predictorDefinition.featureDefinitionId");
  const suppressionFilter = watch("predictorDefinition.suppression_filter");
  const suppressionEnabled = Boolean(suppressionFilter?.eventFilter);

  const { parentModel: queryBuilderParentModel, isLoading } =
    useFetchQueryBuilderParentModel({
      parentModelId: parentModel.id.toString(),
    });

  // Discover key events from the selected catalog's feature definition.
  // Only events that join both the parent model and the catalog are valid
  // suppression targets. We use parentModel for both the feature definition
  // lookup and relationship resolution because the ParentModelForQueryBuilder
  // query filters relationships by to_model.type, which can exclude event
  // models used by catalog recommendations. The EventFilter's inner
  // FilterableOptionsProvider fetches property data independently using
  // condition.eventModelId, so property filtering still works.
  const eventModels = useMemo(() => {
    const featureDef = parentModel.predictor_feature_definitions?.find(
      (pfd) => pfd.id === featureDefinitionId,
    );
    const events = featureDef?.predictor_feature_definition_events ?? [];
    const relationships = parentModel.relationships ?? [];

    const result = events
      .map((event) => {
        const catalog = event.cached_event_pull?.config?.catalog;
        if (!catalog) {
          return null;
        }
        const userRelationshipId = catalog.user_relationship_id as number;
        const rel = relationships.find(
          (r) => Number(r.id) === userRelationshipId,
        );
        if (!rel) {
          return null;
        }
        return {
          keyEventId: event.id,
          eventName: rel.to_model.name,
          eventModelId: Number(rel.to_model.id),
          userRelationshipId,
        };
      })
      .filter((e): e is NonNullable<typeof e> => e != null);

    return result;
  }, [parentModel, featureDefinitionId]);

  if (isLoading) {
    return null;
  }

  const hasKeyEvents = eventModels.length > 0;

  return (
    <QueryBuilderProvider parentModel={queryBuilderParentModel}>
      <Column gap={4}>
        <Row gap={2} align="center">
          <Text fontWeight="medium">Suppression filter</Text>
          <Switch
            isChecked={suppressionEnabled}
            isDisabled={!hasKeyEvents}
            onChange={(value) => {
              if (value && eventModels[0]) {
                setValue(
                  "predictorDefinition.suppression_filter",
                  makeSuppressionFilterValue(eventModels[0]),
                  { shouldDirty: true },
                );
              } else {
                setValue("predictorDefinition.suppression_filter", undefined, {
                  shouldDirty: true,
                });
              }
            }}
            aria-label="Toggle suppression filter"
          />
        </Row>
        <Text size="sm" color="text.secondary">
          {hasKeyEvents
            ? "Suppress recommendations for items that a user has already interacted with."
            : "Add key events to your catalog to enable suppression filtering."}
        </Text>

        {suppressionEnabled && suppressionFilter && hasKeyEvents && (
          <Column gap={4}>
            <FormField label="Event">
              <Select
                value={eventModels.find(
                  (e) => e.keyEventId === suppressionFilter.keyEventId,
                )}
                options={eventModels.map((e) => ({
                  label: e.eventName,
                  value: e,
                }))}
                placeholder="Select an event…"
                onChange={(value) => {
                  if (value) {
                    setValue(
                      "predictorDefinition.suppression_filter",
                      makeSuppressionFilterValue(value),
                      { shouldDirty: true },
                    );
                  }
                }}
              />
            </FormField>

            <Controller
              name="predictorDefinition.suppression_filter.eventFilter"
              render={({ field }) => (
                <EventFilter
                  disableChangingEvent
                  hideFunnelCondition
                  hideOperatorFilter
                  hideWindowCondition
                  condition={field.value}
                  audience={undefined}
                  parent={queryBuilderParentModel}
                  onUpdate={
                    ((updater: UpdaterEnforceDefined<EventCondition>) => {
                      field.onChange(produce(field.value, updater));
                    }) as OnUpdate<EventCondition>
                  }
                />
              )}
            />
          </Column>
        )}
      </Column>
    </QueryBuilderProvider>
  );
}

type ReadOnlyProps = {
  parentModel?: Pick<TraitParentModel, "id" | "name" | "relationships"> & {
    connection: { id: string } | null;
  };
  audienceDefinition?: any;
  featureDefinitionId?: string | null;
  inferenceCadence?: string;
  catalogCollection?: {
    filter: any;
  };
  suppressionFilter?: {
    keyEventId: string;
    eventFilter: any;
  };
};

export function CatalogRecommendationTraitCalculationReadOnly({
  parentModel,
  audienceDefinition,
  catalogCollection,
  featureDefinitionId,
  inferenceCadence,
  suppressionFilter,
}: ReadOnlyProps) {
  const catalogPredictorFeatureDefinitions =
    useGetCatalogPredictorFeatureDefinitionsQuery(
      { parentModelId: parentModel?.id ?? "" },
      { enabled: Boolean(parentModel) },
    );
  const catalog =
    catalogPredictorFeatureDefinitions.data?.predictor_feature_definitions.find(
      (pfd) => pfd.id === featureDefinitionId,
    );

  const rawFilter = catalogCollection?.filter;
  // Normalize: ensure filter has a type if it has conditions.
  // The CatalogFilter form may not initialize the `type` field, causing
  // UnreachableCaseError in ReadOnlyConditions when it renders the filter.
  const filter =
    rawFilter?.conditions?.length > 0
      ? { ...rawFilter, type: rawFilter.type ?? "and" }
      : rawFilter;

  return (
    <Column gap={6} bg="base.background" borderRadius="md" py={3} px={4}>
      <Column gap={2}>
        <Text fontWeight="medium">Parent model</Text>
        <Text>{parentModel?.name}</Text>
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">Catalog</Text>
        <Text>{catalog?.name}</Text>
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">Filters</Text>
        {filter?.conditions && filter?.conditions.length > 0 ? (
          <ReadOnlyConditions
            rootConditions={[filter]}
            audiences={[]}
            traits={[]}
            relationships={parentModel?.relationships || []}
            journeys={[]}
          />
        ) : (
          <Text>No filters</Text>
        )}
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">Eligible users</Text>
        {audienceDefinition?.conditions &&
        audienceDefinition?.conditions.length > 0 ? (
          <ReadOnlyConditions
            rootConditions={[audienceDefinition]}
            audiences={[]}
            traits={[]}
            relationships={parentModel?.relationships || []}
            journeys={[]}
          />
        ) : (
          <Text>No filters</Text>
        )}
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">Suppression filter</Text>
        {suppressionFilter &&
        suppressionFilter.eventFilter?.subconditions?.length > 0 ? (
          <ReadOnlyConditions
            rootConditions={[suppressionFilter.eventFilter]}
            audiences={[]}
            traits={[]}
            relationships={parentModel?.relationships || []}
            journeys={[]}
          />
        ) : (
          <Text>No filters</Text>
        )}
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">How often should scores be updated?</Text>
        <Text>
          {
            predictionUpdateFrequencyOptions.find(
              (option) => option.value === (inferenceCadence ?? "weekly"),
            )?.label
          }
        </Text>
      </Column>
      <Link
        href={`/schema-v2/view/recommendations?source=${parentModel?.connection?.id}&id=${catalog?.catalog_id}`}
        target="_blank"
      >
        View catalog settings
        <ExternalLinkIcon ml={1} />
      </Link>
    </Column>
  );
}
